const toArray = (value) => (Array.isArray(value) ? value : []);

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

export const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const getColumns = (data) => (data.length ? Object.keys(data[0]) : []);

const hasColumn = (data, column) =>
  Boolean(column) && data.some((row) => Object.hasOwn(row, column));

const parseDurationHours = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const match = value.trim().match(/^(\d+):([0-5]?\d):([0-5]?\d)$/);

  if (!match) {
    return null;
  }

  const [, hours, minutes, seconds] = match;

  return Number(hours) + Number(minutes) / 60 + Number(seconds) / 3600;
};

const parseNumericString = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.replaceAll(',', '').trim();

  if (!normalized || Number.isNaN(Number(normalized))) {
    return null;
  }

  return Number(normalized);
};

export const parseTemporalValue = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.getTime();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();

  if (!normalized) {
    return null;
  }

  const isoDate = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (isoDate) {
    const [, year, month, day] = isoDate;

    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const usDate = normalized.match(/^(\d{2})-(\d{2})-(\d{4})$/);

  if (usDate) {
    const [, month, day, year] = usDate;

    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const monthYear = normalized.match(/^([A-Za-z]{3})-(\d{4})$/);

  if (monthYear) {
    const [, monthText, year] = monthYear;
    const monthIndex = MONTH_INDEX[monthText.toLowerCase()];

    if (monthIndex !== undefined) {
      return Date.UTC(Number(year), monthIndex, 1);
    }
  }

  const quarterYear = normalized.match(/^Q([1-4])[- ](\d{4})$/i);

  if (quarterYear) {
    const [, quarter, year] = quarterYear;

    return Date.UTC(Number(year), (Number(quarter) - 1) * 3, 1);
  }

  const parsed = Date.parse(normalized);

  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  return null;
};

const getFieldSamples = (data, field) =>
  data
    .map((row) => row[field])
    .filter((value) => value !== undefined && value !== null && value !== '');

const isNumericField = (data, field) => {
  const samples = getFieldSamples(data, field);

  if (!samples.length) {
    return false;
  }

  return samples.every(
    (value) =>
      typeof value === 'number' ||
      parseNumericString(value) !== null ||
      parseDurationHours(value) !== null
  );
};

const isTemporalField = (data, field) => {
  if (!field) {
    return false;
  }

  if (/(date|month|year|quarter|week|day|time)$/i.test(field)) {
    return true;
  }

  const samples = getFieldSamples(data, field);

  if (!samples.length) {
    return false;
  }

  const temporalCount = samples.filter(
    (value) => parseTemporalValue(value) !== null
  ).length;

  return temporalCount > 0 && temporalCount === samples.length;
};

const pickYAxis = ({ data, requestedYaxis }) => {
  const columns = getColumns(data);

  if (hasColumn(data, requestedYaxis) && isNumericField(data, requestedYaxis)) {
    return requestedYaxis;
  }

  return (
    columns.find((column) => isNumericField(data, column)) ||
    requestedYaxis ||
    null
  );
};

const pickXAxis = ({ data, requestedXaxis, yaxis, groupBy }) => {
  const columns = getColumns(data);
  const categoricalColumns = columns.filter(
    (column) => column !== yaxis && column !== groupBy
  );

  if (hasColumn(data, requestedXaxis)) {
    return requestedXaxis;
  }

  return (
    categoricalColumns.find((column) => isTemporalField(data, column)) ||
    categoricalColumns[0] ||
    columns.find((column) => column !== yaxis) ||
    requestedXaxis ||
    null
  );
};

const pickGroupBy = ({ data, requestedGroupBy, xaxis, yaxis, chartType }) => {
  const columns = getColumns(data);
  const candidates = columns.filter(
    (column) =>
      column !== xaxis && column !== yaxis && !isNumericField(data, column)
  );

  if (
    hasColumn(data, requestedGroupBy) &&
    requestedGroupBy !== xaxis &&
    requestedGroupBy !== yaxis
  ) {
    return requestedGroupBy;
  }

  if (String(chartType || '').toUpperCase() === 'LINE') {
    return candidates[0] || null;
  }

  return null;
};

const normalizeChartData = ({ data, yaxis }) => {
  if (!yaxis || !data.length) {
    return {
      chartData: data,
      yaxis,
      valueFormat: null,
      originalYaxis: yaxis,
    };
  }

  const durationValues = data.map((row) => parseDurationHours(row[yaxis]));
  const hasDurationValues = durationValues.some((value) => value !== null);
  const allDurationValues =
    hasDurationValues &&
    durationValues.every(
      (value, index) =>
        value !== null ||
        data[index][yaxis] === null ||
        data[index][yaxis] === undefined
    );

  if (allDurationValues) {
    const numericAxis = `${yaxis}_hours`;

    return {
      chartData: data.map((row, index) => ({
        ...row,
        [numericAxis]: durationValues[index],
      })),
      yaxis: numericAxis,
      valueFormat: 'hours',
      originalYaxis: yaxis,
    };
  }

  const numericValues = data.map((row) => parseNumericString(row[yaxis]));
  const hasNumericValues = numericValues.some((value) => value !== null);
  const allNumericValues =
    hasNumericValues &&
    numericValues.every(
      (value, index) =>
        value !== null ||
        data[index][yaxis] === null ||
        data[index][yaxis] === undefined
    );

  if (allNumericValues) {
    const numericAxis = `${yaxis}_number`;

    return {
      chartData: data.map((row, index) => ({
        ...row,
        [numericAxis]: numericValues[index],
      })),
      yaxis: numericAxis,
      valueFormat: null,
      originalYaxis: yaxis,
    };
  }

  return {
    chartData: data,
    yaxis,
    valueFormat: null,
    originalYaxis: yaxis,
  };
};

const normalizeChartType = ({
  chartType,
  visualizationType,
  groupBy,
  xaxisType,
}) => {
  const normalizedViz = String(visualizationType || '')
    .trim()
    .toUpperCase();

  if (normalizedViz === 'TABLE') {
    return 'TABLE';
  }

  const normalized = String(chartType || '')
    .trim()
    .replaceAll('-', '_')
    .replaceAll(' ', '_')
    .toUpperCase();

  if (normalized === 'TABLE') {
    return 'TABLE';
  }

  if (normalized === 'GROUPED_BAR') {
    return 'GROUPED_BAR';
  }

  if (normalized === 'BAR') {
    return groupBy ? 'GROUPED_BAR' : 'BAR';
  }

  if (normalized === 'LINE') {
    return 'LINE';
  }

  if (normalized === 'AREA') {
    return 'LINE';
  }

  if (groupBy) {
    return xaxisType === 'time' ? 'LINE' : 'GROUPED_BAR';
  }

  return xaxisType === 'time' ? 'LINE' : 'BAR';
};

const buildKpis = ({ response, data }) => {
  const kpis = [];

  if (response.rowCount !== undefined) {
    kpis.push({
      name: 'Rows returned',
      value: response.rowCount.toLocaleString(),
    });
  }

  if (response.executionTimeMs !== undefined) {
    kpis.push({
      name: 'Execution time',
      value: `${response.executionTimeMs.toLocaleString()} ms`,
    });
  }

  if (response.status) {
    kpis.push({
      name: 'Status',
      value: response.status,
    });
  }

  if (!kpis.length && data.length) {
    kpis.push({
      name: 'Rows returned',
      value: data.length.toLocaleString(),
    });
  }

  return kpis;
};

export const normalizeApiResponse = (response) => {
  if (!response) {
    return response;
  }

  const insightConfig =
    response.insights && !Array.isArray(response.insights)
      ? response.insights
      : {};

  const chartConfig = response.chartConfig || {};
  const data = firstDefined(response.data, response.results, []);
  const requestedYaxis = firstDefined(insightConfig.yaxis, chartConfig.yaxis);
  const provisionalYAxis = pickYAxis({
    data,
    requestedYaxis,
  });
  const requestedGroupBy = firstDefined(
    insightConfig.groupBy,
    chartConfig.groupBy
  );
  const requestedXaxis = firstDefined(insightConfig.xaxis, chartConfig.xaxis);
  const provisionalXAxis = pickXAxis({
    data,
    requestedXaxis,
    yaxis: provisionalYAxis,
    groupBy: requestedGroupBy,
  });
  const groupBy = pickGroupBy({
    data,
    requestedGroupBy,
    xaxis: provisionalXAxis,
    yaxis: provisionalYAxis,
    chartType: firstDefined(insightConfig.chartType, response.chartType),
  });
  const xaxis = pickXAxis({
    data,
    requestedXaxis,
    yaxis: provisionalYAxis,
    groupBy,
  });
  const normalizedChartData = normalizeChartData({
    data,
    yaxis: provisionalYAxis,
  });
  const xaxisType = isTemporalField(data, xaxis) ? 'time' : 'category';
  const chartType = normalizeChartType({
    chartType: firstDefined(insightConfig.chartType, response.chartType),
    visualizationType: firstDefined(
      insightConfig.visualizationType,
      response.visualizationType
    ),
    groupBy,
    xaxisType,
  });

  return {
    ...response,
    answer: firstDefined(response.answer, insightConfig.summary),
    summary: firstDefined(response.summary, insightConfig.summary),
    insights: Array.isArray(response.insights)
      ? response.insights
      : toArray(insightConfig.insights),
    recommendations: firstDefined(
      response.recommendations,
      insightConfig.recommendations,
      []
    ),
    warnings: firstDefined(response.warnings, insightConfig.warnings, []),
    generatedSql: firstDefined(response.generatedSql, response.queryPlan?.sql),
    chartType,
    chartConfig: {
      title: firstDefined(insightConfig.title, chartConfig.title),
      description: firstDefined(
        chartConfig.description,
        response.question,
        data.length ? `${data.length} rows returned` : null
      ),
      groupBy,
      xaxis,
      yaxis: normalizedChartData.yaxis,
      originalYaxis: normalizedChartData.originalYaxis,
      valueFormat: normalizedChartData.valueFormat,
      xaxisType,
    },
    chartData: normalizedChartData.chartData,
    data,
    kpis: firstDefined(response.kpis, buildKpis({ response, data })),
    queryParameters: response.queryPlan?.parameters || response.queryParameters,
  };
};
